import { getFeeListBasedOnEffeciveDate } from '../../../../components/tax/common-utils/CommonTaxFunctionalities';
import { MIN_ALLOW_AMT_HALF } from '../../../../components/tax/common-utils/constants';
import {
  DEFAULT_TAX_PAYMENT_TYPE, exemptManufacturingStatusCode, getTaxYearStatus,
  partitionPendingPaymentAlertCodes, penaltyCalculation,
} from '../../../../components/tax/common-utils/tax-common.util';
import { toShortDate } from '../../../../util/shared/tmc-global';
import { amountFormat, amountToNumber } from '../../../../util/shared/vue-global';

/**
 * Resolving a keyed Tax Yr + Tax ID into a payment row happens in two places on the
 * large-payment screen - TaxEditDialog retargets the row it is editing, and LargePaymentList
 * appends a new row from the payment dialog's header fields. The penalty / fee / half-pay math
 * below is the money part of that, so it lives here once rather than in both.
 *
 * Extracted verbatim from TaxEditDialog.applyTaxInquiryToRow, which mirrored the payment
 * screen's commonLoadTaxInquiryFunc.
 */

/**
 * Parcel IDs are stored uppercase with no hyphens; normalize the keyed value to that form so
 * lower case, hyphens and stray whitespace all resolve to the same record set. Same rule
 * dialogTaxDetails.normalizeParcelId applies.
 */
export function normalizeParcelId(rawValue) {
  return (rawValue || '').toString().replace(/[\s-]/g, '').toUpperCase();
}

/**
 * Whether `row` is already this resolved record ON THIS PAYMENT TYPE - the duplicate guard both
 * header lookups apply before they price a record and put it on the payment. `taxInquiryId`
 * covers rows a lookup resolved, taxYear + taxId covers rows that came off a saved payment.
 *
 * The type is part of it deliberately, and for the same reason LargePaymentList.taxRowIdentity
 * (and the server's own findDuplicateLiveTaxDetail) include it: one record may legitimately sit
 * on a payment as a payment line AND as a refund line, which is the whole point of keying the
 * type in the Add Tax Records band. The same record twice on the same type is still refused.
 */
export function isSameRecordAndType(row, resolved, taxType) {
  const asked = taxType || DEFAULT_TAX_PAYMENT_TYPE;
  if (((row || {}).taxType || DEFAULT_TAX_PAYMENT_TYPE) !== asked) return false;
  if (row.taxInquiryId && row.taxInquiryId === resolved._id) return true;
  return Number(row.taxYear) === Number(resolved.taxYear)
    && Number(row.taxId) === Number(resolved.taxId);
}

/**
 * Tax ID or parcel ID, decided by length exactly as the payment screen does. Returns null when
 * there is nothing to look up, so callers can bail without repeating the checks.
 */
/**
 * The id an unsaved row answers to until the payment is saved. The grid identifies rows by
 * `_id` - it is CRUDList's item-value, and it is what both the selection and the per-row edit
 * match on - so every unsaved row needs one of its own, and the row's natural key here is what
 * makes it distinct: the tax year and id, protested or not, and the PAYMENT TYPE.
 *
 * The type is part of it because one record may legitimately sit on a payment twice, as a
 * payment line and as a refund line (the duplicate guards key on the type for the same reason).
 * Without it both lines answered to the same key: ticking one ticked both, a Half Pay over the
 * selection halved both, and an edit or a void could land on the wrong one.
 *
 * The `new-` prefix keeps it from being mistaken for a stored id. deleteFieldsNotRequireToUpsert
 * drops it from every itsNewRecord row on save, so it never reaches the server.
 */
export function newTaxRowId({
  taxYear, taxId, protest, taxType,
}) {
  return `new-${taxYear}-${taxId}-${protest ? 'p' : 't'}-${taxType || 'TAXPAYMENT'}`;
}

/** Whether `_id` is one of the client-side ids above rather than a stored one. */
export function isNewTaxRowId(id) {
  return typeof id === 'string' && id.startsWith('new-');
}

export function buildTaxLookupCriteria(rawTaxYear, rawTaxId) {
  const taxYear = amountToNumber(rawTaxYear);
  const taxIdString = (rawTaxId === null || rawTaxId === undefined)
    ? '' : String(rawTaxId).trim();
  if (!taxYear || !taxIdString) return null;

  const criteria = { taxYear };
  if (taxIdString.length <= 7) {
    criteria.taxId = amountToNumber(taxIdString);
  } else {
    criteria.pIdExactMatch = taxIdString.replace(/-/g, '');
  }
  return criteria;
}

/**
 * Builds the payment row for a resolved taxinquiry record: owner, tax amount, penalty, fees,
 * total, plus the supporting values the Half toggle, the total recalcs and the upsert read back.
 *
 * @param {Object}   newData               the taxinquiry record the lookup resolved to
 * @param {Object}   baseRow               row the values are merged onto (the row being edited,
 *                                         or a fresh row for an append)
 * @param {String}   penaltyEffectiveDate  ISO date the penalty and fees are effective on
 * @param {Object}   taxSettingItem        SystemConfig.taxSettingItem
 * @param {Object}   paymentSettingItem    SystemConfig.paymentSettingItem
 * @param {Boolean}  dialogHalf            the dialog's Half Pay toggle
 * @param {Function} loadTaxRollFeesTable  tax/taxFee action, called for the effective fees
 * @returns {Promise<Object>} the merged row
 */
export async function buildTaxRowFromInquiry({
  newData,
  baseRow = {},
  penaltyEffectiveDate,
  taxSettingItem,
  paymentSettingItem,
  dialogHalf = false,
  loadTaxRollFeesTable,
}) {
  const {
    totalTax, totalPaid, penaltyStartDate, taxYear, totalDue, taxId, schoolDistrict, mtgCode,
    exemptions, _id, baseId, statusCode, name1, name2, certificate, land, improvements,
    mobileHome, assessorPenalty, mhVin, mhDecal,
  } = newData;
  // 406 zeroes out both penalty and fees.
  const isExemptManufacturing = (statusCode || []).includes(exemptManufacturingStatusCode);

  // Fees effective on the penalty date. Keyed off the RESOLVED taxYear/taxId rather than what
  // the clerk typed - a parcel-ID search resolves to a taxId here, and the fee table is only
  // queryable by taxYear + taxId (same as the payment screen).
  let feeDetails = [];
  let feeAmt = 0;
  let allFeesList = [];
  if (!isExemptManufacturing) {
    const feeData = await loadTaxRollFeesTable(
      { criteria: { taxYear, taxId, paymentNotDone: true } },
    );
    if ((feeData || []).length) {
      allFeesList = feeData;
      const { feeDet, feesAmt } = getFeeListBasedOnEffeciveDate(feeData, penaltyEffectiveDate);
      feeDetails = feeDet;
      feeAmt = feesAmt;
    }
  }

  const { penaltyDate, penaltyRate } = taxSettingItem || {};
  const returnedVal = penaltyCalculation(
    toShortDate(penaltyEffectiveDate),
    toShortDate(penaltyStartDate),
    penaltyDate,
    totalTax,
    totalPaid,
    0,
    penaltyRate,
    totalDue,
    taxYear,
    (statusCode || []),
    taxSettingItem,
  );

  const totalGrossAssessed = (land || 0) + (improvements || 0) + (mobileHome || 0)
    + (assessorPenalty || 0);

  // A special assessment follows the regular Half rules only when its tax district opted in
  // (allowHalfPayments); every other S record stays pay-in-full.
  const isHalfBlockedSa = newData.taxType === 'S' && !newData.allowHalfPayments;
  // Half stays available only when there is no penalty, no fee, no blocked special assessment
  // and the balance clears the minimum - same gate as the payment screen.
  const blocksHalf = !!amountToNumber(returnedVal.penaltyAmt) || !!amountToNumber(feeAmt)
    || isHalfBlockedSa || totalDue <= MIN_ALLOW_AMT_HALF;
  const allowPartial = (paymentSettingItem || {}).allowPartialPayemnts
    && totalDue > MIN_ALLOW_AMT_HALF && !isHalfBlockedSa;
  // The county's Allow Partial Payments opt-in wins over the penalty/fee gate, exactly as it
  // does on the payment screen (dialogTaxDetails): a county that has opted in wants Half on its
  // delinquents, fee-bearing ones included (Washington, TKT-000894). allowPartial still requires
  // the minimum and excludes special assessments, so those stay blocked either way.
  const disableHalf = allowPartial ? false : blocksHalf;
  // Half follows the same eligibility as the toggle itself (disableHalf), never a second,
  // stricter one. It used to read `!blocksHalf && !allowPartial`, which is false for EVERY row
  // in an opted-in county - so Tulsa could turn Half on, scan a current record and still get the
  // full amount on the line (TKT 10/23: 2026/220 priced at 5,283.00 instead of 2,641.50). The
  // row was allowed to halve and never did.
  const keepHalf = !disableHalf && !!(baseRow.Half || dialogHalf);

  const taxAmtVal = keepHalf
    ? amountFormat(amountToNumber(totalTax) / 2)
    : amountFormat(totalDue);

  // Back-tax rows are not partially payable unless the setting allows it.
  const taxStatus = getTaxYearStatus();
  const isBackTax = amountToNumber(taxYear) <= amountToNumber(taxStatus.backTax)
    && !(paymentSettingItem || {}).allowPartialPayemnts;

  /**
   * The audit baseline - the copy applyAuditForPayment compares the row against on save. It has
   * to be stamped HERE, with the values the lookup just produced: without it every freshly
   * loaded row audits its own penalty as a change ("Penalty changes from 0.00 to X") and every
   * fee on it as newly added, because amountToNumber(undefined) is 0.
   * Same six values, and the same itsNewRecord gate, as the payment screen stamps at the end of
   * commonLoadTaxInquiryFunc. A row that is not new keeps whatever baseline it came with.
   * initialFees is a copy rather than the same array as fees - sharing the reference would let
   * a later fee edit mutate the baseline too, and the audit would miss the change.
   */
  const auditBaseline = baseRow.itsNewRecord ? {
    initialPenaltyAmt: amountToNumber(returnedVal.penaltyAmt),
    initialTaxAmt: amountToNumber(taxAmtVal),
    initialFees: feeDetails.map((fee) => ({ ...fee })),
    initialTotalFees: amountToNumber(feeAmt),
    initialTaxYear: taxYear,
    initialTaxId: taxId,
  } : {};

  return {
    ...baseRow,
    ...auditBaseline,
    // A row keyed in here has no server id yet - see newTaxRowId for what stands in for one.
    _id: baseRow._id || newTaxRowId({
      taxYear, taxId, protest: baseRow.protest, taxType: baseRow.taxType,
    }),
    taxYear,
    ownerName: `${name1 || ''} ${name2 || ''}`.trim(),
    // the roll record's taxType is the property tax type (R/P/B/...) the Tax Type column and the
    // decal prompt show; the row's own `taxType` is the payment type (Tax Payment, Refund...)
    propertyTaxType: newData.taxType,
    schoolDistrict,
    taxAmt: taxAmtVal,
    penaltyAmt: amountFormat(amountToNumber(returnedVal.penaltyAmt)),
    totalFees: isExemptManufacturing ? amountFormat(0) : amountFormat(feeAmt),
    total: amountFormat(amountToNumber(returnedVal.penaltyAmt)
      + amountToNumber(taxAmtVal) + amountToNumber(feeAmt)),
    fees: feeDetails,
    allFeesList,
    statusCode: isExemptManufacturing ? [exemptManufacturingStatusCode] : [],
    // Supporting values the Half toggle and the total recalcs downstream read back.
    totalTax,
    totalPaid,
    taxTotalDue: totalDue,
    penaltyStartDate,
    penaltyChangedManually: false,
    baseId,
    taxInquiryId: _id,
    originalTaxId: taxId,
    originalTaxAmt: amountToNumber(totalTax),
    originalTaxAmount: amountToNumber(totalTax),
    originalGrossAssessed: totalGrossAssessed,
    grossAssessed: keepHalf ? totalGrossAssessed / 2 : totalGrossAssessed,
    exemptions,
    mtgCode,
    mobileHome,
    // for the decal prompt (PaymentCommonFunc.mixin mobileHomeArr), read off the same lookup so
    // it needs no second query; the mixin strips both before the payment is sent
    mhVin,
    mhDecal,
    certNumber: certificate || null,
    Half: keepHalf,
    disableHalf,
    allowBackTaxEdit: isBackTax,
  };
}

/**
 * Whether the record carries a 416/419 (Pending Payment / Pending Web Payment) alert code, and
 * so needs the pending-payment details fetched before its warnings can be built.
 */
export function hasPendingPaymentAlertCode(alertStatusCodes) {
  return partitionPendingPaymentAlertCodes(alertStatusCodes || []).pendingCodes.length > 0;
}

/**
 * The warnings a resolved record raises, as the payment screen's commonLoadTaxInquiryFunc raises
 * them: alert status codes, the mortgage code (setting-gated), no taxes due, and an existing
 * unposted payment. Returned as { txtName, txtMsg } so the popup can colour each line the way
 * handleCircleClass already does - red for the two that mean the money may already be collected.
 *
 * Pure: the caller fetches existingPayments (loadPayment) and pendingPaymentDetails
 * (getPendingPaymentDetails, only when hasPendingPaymentAlertCode says so).
 *
 * Unlike the payment screen, 416/419 does not get its own separate modal here - the pending
 * payment number and date go into the same list, so one popup covers a whole parcel's worth of
 * records. Every message is prefixed with the record it belongs to for that reason.
 */
export function collectTaxRowWarnings({
  newData,
  existingPayments = [],
  pendingPaymentDetails = [],
  paymentSettingItem,
}) {
  const {
    alertStatusCodes, mtgCode, taxYear, taxId, totalDue,
  } = newData;
  const warnings = [];
  const forRecord = `Tax Yr ${taxYear} Tax ID ${taxId}`;

  if ((alertStatusCodes || []).length) {
    const { pendingCodes, otherCodes } = partitionPendingPaymentAlertCodes(alertStatusCodes);
    if (pendingCodes.length && (pendingPaymentDetails || []).length) {
      pendingPaymentDetails.forEach((detail) => {
        warnings.push({
          txtName: 'extraPayment',
          txtMsg: `${forRecord} has a pending payment - Payment#: ${detail.paymentNumber}, Date: ${toShortDate(detail.payDate)}.`,
        });
      });
    } else if (pendingCodes.length) {
      // stale flag: no pending payment behind it after all - report it as a status code
      otherCodes.push(...pendingCodes);
    }
    if (otherCodes.length) {
      warnings.push({
        txtName: 'statusCodePayment',
        txtMsg: `${forRecord}: this record has a status code of ${otherCodes}.`,
      });
    }
  }

  if ((paymentSettingItem || {}).warnWhenPayingRecWithMtg && mtgCode) {
    warnings.push({
      txtName: 'mtgPayment',
      txtMsg: `${forRecord} is coded with the mortgage code ${mtgCode}.`,
    });
  }

  if (!totalDue) {
    warnings.push({
      txtName: 'alreadyPayExits',
      txtMsg: `${forRecord}: this record has no taxes due.`,
    });
  }

  if ((existingPayments || []).length) {
    const first = existingPayments[0];
    warnings.push({
      txtName: 'extraPayment',
      txtMsg: `${forRecord}: there is a pending payment on this tax record - Date: ${first.payDate ? toShortDate(first.payDate) : ''}, Payment#: ${first.paymentNumber || ''}, Wkst#: ${first.workstation || ''} for $${amountFormat(totalDue)}.`,
    });
  }

  return warnings;
}

export default {
  normalizeParcelId,
  newTaxRowId,
  isNewTaxRowId,
  isSameRecordAndType,
  buildTaxLookupCriteria,
  buildTaxRowFromInquiry,
  hasPendingPaymentAlertCode,
  collectTaxRowWarnings,
};
